import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, X } from "lucide-react";
import { UserRole } from "@/types";
import { SubscriptionPlan } from "@/types/subscription";

type UserFilterProps = {
  filters: {
    search: string;
    status: string;
    role: string;
    plan: string;
    expiry: string;
  };
  searchInput?: string;
  onFilterChange: (name: string, value: string) => void;
  onResetFilters: () => void;
  userRoles: UserRole[];
  subscriptionPlans?: SubscriptionPlan[];
};

const UserFilter = ({
  filters,
  searchInput,
  onFilterChange,
  onResetFilters,
  userRoles,
  subscriptionPlans = [],
}: UserFilterProps) => {
  return (
    <div className="bg-card rounded-lg">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 p-4">
        {/* Search Field - Takes 4 columns on desktop */}
        <div className="sm:col-span-2 lg:col-span-3">
          <Label htmlFor="search" className="text-sm font-medium mb-1.5 block">
            Search
          </Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              id="search"
              placeholder="Search users..."
              className="pl-10"
              value={searchInput !== undefined ? searchInput : filters.search}
              onChange={(e) => onFilterChange("search", e.target.value)}
            />
          </div>
        </div>

        {/* Status Select - Takes 2 columns on desktop */}
        <div className="sm:col-span-1 lg:col-span-2">
          <Label htmlFor="status" className="text-sm font-medium mb-1.5 block">
            Status
          </Label>
          <Select
            value={filters.status || "all"}
            onValueChange={(value) =>
              onFilterChange("status", value === "all" ? "" : value)
            }
          >
            <SelectTrigger id="status" className="w-full">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Plan Select - Takes 2 columns on desktop */}
        <div className="sm:col-span-1 lg:col-span-2">
          <Label htmlFor="plan" className="text-sm font-medium mb-1.5 block">
            Plan
          </Label>
          <Select
            value={filters.plan || "all"}
            onValueChange={(value) =>
              onFilterChange("plan", value === "all" ? "" : value)
            }
          >
            <SelectTrigger id="plan" className="w-full">
              <SelectValue placeholder="All Plans" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Plans</SelectItem>
              <SelectItem value="none">No Plan</SelectItem>
              {subscriptionPlans
                .filter((plan) => plan.isActive)
                .map((plan) => (
                  <SelectItem key={plan.id} value={String(plan.id)}>
                    {plan.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        {/* Expiry Status Select */}
        <div className="sm:col-span-1 lg:col-span-2">
          <Label htmlFor="expiry" className="text-sm font-medium mb-1.5 block">
            Expiry
          </Label>
          <Select
            value={filters.expiry || "all"}
            onValueChange={(value) =>
              onFilterChange("expiry", value === "all" ? "" : value)
            }
          >
            <SelectTrigger id="expiry" className="w-full">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="expiring-soon">Expiring Soon</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="none">None</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Role Select - Takes 2 columns on desktop */}
        <div className="sm:col-span-1 lg:col-span-2">
          <Label htmlFor="role" className="text-sm font-medium mb-1.5 block">
            Role
          </Label>
          <Select
            value={filters.role || "all"}
            onValueChange={(value) =>
              onFilterChange("role", value === "all" ? "" : value)
            }
          >
            <SelectTrigger id="role" className="w-full">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {userRoles.map((role) => (
                <SelectItem key={role.id} value={String(role.id)}>
                  {role.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Reset Button - Takes 1 column on desktop */}
        <div className="sm:col-span-1 lg:col-span-1 flex items-end">
          <Button
            variant="outline"
            onClick={onResetFilters}
            className="w-full"
            size="default"
          >
            Reset
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserFilter;
